
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, Navigate } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { Stars, Moon, Luggage, Download, MapPin, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { OptimizedImage } from '@/components/OptimizedImage';
import { stripSessionIdFromUrl, trackPurchase } from '@/lib/analytics';

const Success = () => {
  const [searchParams] = useSearchParams();
  const { user, checkSubscription } = useAuth();
  const sessionId = searchParams.get('session_id');
  const [setupComplete, setSetupComplete] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    const checkoutSessionId = sessionId;

    const completeSetupAndSignIn = async () => {
      try {
        // Complete the payment setup with retries
        // Stripe may not have finalized the session by the time we land here
        let setupSuccess = false;
        for (let attempt = 1; attempt <= 3; attempt++) {
          const response = await supabase.functions.invoke('complete-user-setup', {
            body: { sessionId }
          });
          if (response.error) {
            console.error(`[SUCCESS] Attempt ${attempt} failed:`, response.error?.message || response.error);
            if (attempt < 3) {
              await new Promise(r => setTimeout(r, 3000)); // Wait 3s before retry
            }
          } else {
            setupSuccess = true;
            // Never rejects; a tracking failure must not skip session restore or the subscription refresh.
            void trackPurchase(checkoutSessionId, 'Premium', response.data);
            break;
          }
        }

        if (setupSuccess) {
          stripSessionIdFromUrl();
        }

        // Restore session using stored tokens (no password stored)
        const tempCredentials = sessionStorage.getItem('tempCredentials');
        if (tempCredentials) {
          const parsed = JSON.parse(tempCredentials);
          if (parsed.accessToken && parsed.refreshToken) {
            await supabase.auth.setSession({
              access_token: parsed.accessToken,
              refresh_token: parsed.refreshToken,
            });
          }
          sessionStorage.removeItem('tempCredentials');
        }

        // Refresh subscription state
        await checkSubscription();
        setSetupComplete(true);
      } catch (error) {
        console.error('Error completing setup:', error);
        setSetupComplete(true); // Show page anyway
      }
    };

    completeSetupAndSignIn();
  }, [sessionId]);

  const planners = [
    {
      title: 'Milky Way Calendar',
      description: 'Every night rated 0–10 for Milky Way photography. Galactic core rise/set times, moon phase, and visibility windows for your exact location.',
      icon: Stars,
      image: '/lovable-uploads/screenshot-milkyway-calendar.webp',
      href: '/dashboard?tab=milkyway',
      cta: 'Open Milky Way Calendar',
    },
    {
      title: 'Moon Planner',
      description: 'Moonrise and moonset times with compass directions, supermoon dates, and golden hour overlaps. Know exactly when the moon will be in position.',
      icon: Moon,
      image: '/lovable-uploads/screenshot-moon-planner.webp',
      href: '/dashboard?tab=moon',
      cta: 'Open Moon Planner',
    },
    {
      title: 'Trip Planner',
      description: 'Build multi-stop photography itineraries with date-specific visibility for each location. Plan an entire week or month of shooting in one place.',
      icon: Luggage,
      image: '/lovable-uploads/screenshot-trip-planner.webp',
      href: '/dashboard?tab=trips',
      cta: 'Open Trip Planner',
    },
  ];

  const extras = [
    { icon: Download, label: 'Export to PDF, Excel, CSV, and ICS calendar files' },
    { icon: MapPin, label: 'Save unlimited favorite locations for quick access' },
    { icon: Calendar, label: 'Full yearly planning view across all months' },
  ];

  // Redirect if no session_id and not logged in. The static build hits this
  // branch, so it still needs a title, description, canonical and one h1.
  if (!sessionId && !user) {
    return (
      <>
        <SEOHead
          title="Welcome to Premium | Milky Way Planner"
          description="Your Milky Way Planner Premium account is active. Moon Planner, Trip Planner, and PDF, Excel, CSV, and calendar exports are unlocked."
          noindex
        />
        <h1 className="text-4xl md:text-5xl text-foreground mb-4 tracking-[-0.03em]">Welcome to Premium</h1>
        <Navigate to="/" replace />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title="Welcome to Premium | Milky Way Planner"
        description="Your Milky Way Planner Premium account is active. Moon Planner, Trip Planner, and PDF, Excel, CSV, and calendar exports are unlocked."
        noindex
      />
      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-12 pb-10 text-center">
          <h1 className="text-4xl md:text-5xl text-foreground mb-4 tracking-[-0.03em]">
            Welcome to Premium
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed mb-6">
            Your account is active and all premium features are unlocked.
            {user?.email && (
              <> Signed in as <span className="text-foreground font-medium">{user.email}</span>.</>
            )}
          </p>
          <Link to="/dashboard">
            <Button size="lg">
              Go to Your Dashboard
            </Button>
          </Link>
          {!setupComplete && sessionId && (
            <p className="text-sm text-muted-foreground mt-3 animate-pulse">
              Finishing setup...
            </p>
          )}
        </section>

        {/* Planners */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="text-lg font-semibold text-foreground mb-8 text-center">
            Your three planners are ready
          </h2>

          <div className="space-y-14">
            {planners.map((planner, i) => {
              const Icon = planner.icon;
              const reversed = i % 2 === 1;
              return (
                <div
                  key={planner.title}
                  className={`grid md:grid-cols-2 gap-8 md:gap-12 items-center`}
                >
                  <div className={`overflow-hidden rounded-[4px] border border-border shadow-lg ${reversed ? 'md:order-2' : ''}`}>
                    <OptimizedImage
                      src={planner.image}
                      alt={planner.title}
                      width={640}
                      className="w-full"
                    />
                  </div>
                  <div className={`space-y-4 ${reversed ? 'md:order-1' : ''}`}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5 text-gold" />
                      <h3 className="text-xl font-semibold text-foreground">{planner.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      {planner.description}
                    </p>
                    <Link to={planner.href}>
                      <Button variant="outline" className="mt-2">
                        {planner.cta}
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Extras */}
        <section className="border-t border-border">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
            <h2 className="text-lg font-semibold text-foreground mb-6 text-center">
              Plus everything else
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {extras.map((extra) => {
                const Icon = extra.icon;
                return (
                  <div key={extra.label} className="flex items-start gap-3">
                    <Icon className="w-5 h-5 text-gold mt-0.5 shrink-0" />
                    <p className="text-sm text-muted-foreground">{extra.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default Success;
